/* src/pages/HousekeepingReportsPage.js */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Alert,
  notification,
  Descriptions,
  Tag,
  Spin,
  Statistic,
  Modal,
  List
} from 'antd';
import {
  LineChartOutlined,
  ReloadOutlined,
  ToolOutlined,
  WarningOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';

const { Title, Text } = Typography;

const HousekeepingReportsPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [deepDiagnosticData, setDeepDiagnosticData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [error, setError] = useState(null);
  const [showFixModal, setShowFixModal] = useState(false);

  // Summary data from GET /api/HousekeepingReport
  const [reportSummary, setReportSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // ---- API calls ----------------------------------------------------------

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      // HotelId comes from claims on the API; we just call the endpoint.
      const response = await axiosInstance.get('/HousekeepingReport');
      setReportSummary(response.data);
    } catch (err) {
      console.error('Summary load failed:', err);
      notification.error({
        message: 'Failed to load housekeeping summary',
        description: err.response?.data?.message || err.message
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const runDeepDiagnostic = async () => {
    if (!hotelId) {
      notification.error({
        message: 'No Hotel ID',
        description: 'Hotel ID is missing'
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        '/Housekeepingreport/Report/deep-diagnostic',
        {
          params: { hotelId }
        }
      );

      setDeepDiagnosticData(response.data);
      notification.success({
        message: 'Deep Diagnostic Completed',
        description: `Found ${response.data.taskAnalysis?.totalTasks || 0} tasks`
      });
    } catch (err) {
      console.error('Deep diagnostic failed:', err);
      setError(err.response?.data || err.message);
      notification.error({
        message: 'Deep Diagnostic Failed',
        description: 'Could not analyze data relationships'
      });
    } finally {
      setLoading(false);
    }
  };

  const fixDataIssues = async () => {
    if (!hotelId) return;

    setFixing(true);
    try {
      const response = await axiosInstance.post(
        '/Housekeepingreport/Report/fix-data-issues',
        null,
        {
          params: { hotelId }
        }
      );

      notification.success({
        message: 'Data Issues Fixed',
        description: `Fixed ${response.data.fixedCount} issues`
      });

      // Refresh diagnostic data
      runDeepDiagnostic();
      setShowFixModal(false);
    } catch (err) {
      console.error('Fix failed:', err);
      notification.error({
        message: 'Fix Failed',
        description: err.response?.data?.error || 'Could not fix data issues'
      });
    } finally {
      setFixing(false);
    }
  };

  const refreshAll = () => {
    loadSummary();
    runDeepDiagnostic();
  };

  useEffect(() => {
    if (hotelId) {
      loadSummary();
      runDeepDiagnostic();
    }
  }, [hotelId]);

  if (!hotelId) {
    return (
      <Alert
        message="Hotel ID Missing"
        description="Please ensure you are logged in and have a valid hotel ID."
        type="error"
        showIcon
      />
    );
  }

  const daily = reportSummary?.dailySummary;
  const staffPerformance = reportSummary?.staffPerformance || [];

  // Detailed list of rooms that were cleaned (from diagnostic sample tasks)
  const cleanedTasks =
    deepDiagnosticData?.taskAnalysis?.sampleTasks?.filter(
      (task) => task.Status === 'Completed'
    ) || [];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <LineChartOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Housekeeping Reports &amp; Data Diagnostics
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ToolOutlined />}
              loading={fixing}
              onClick={() => setShowFixModal(true)}
              danger
            >
              Fix Data Issues
            </Button>
            <Button
              icon={<ReloadOutlined />}
              loading={loading || summaryLoading}
              onClick={refreshAll}
              type="primary"
            >
              Refresh
            </Button>
          </Space>
        }
        variant="outlined"
      >
        {/* Loading indicator */}
        {(loading || summaryLoading) && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin size="large" />
            <div style={{ marginTop: 10 }}>Loading housekeeping data...</div>
          </div>
        )}

        {/* Error for diagnostics */}
        {error && (
          <Alert
            message="Diagnostic Error"
            description={
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(error, null, 2)}
              </pre>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* ---- Summary section (GET /HousekeepingReport) ---- */}
        {reportSummary && (
          <Card
            title={
              <Space>
                <DatabaseOutlined />
                <span>Operational Summary</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
            variant="outlined"
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic
                  title="Rooms Cleaned"
                  value={daily?.roomsCleaned || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Rooms Pending"
                  value={daily?.roomsPending || 0}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Rooms Under Maintenance / In Progress"
                  value={daily?.roomsUnderMaintenance || 0}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>

            {/* ---- Staff Performance (grouped by staff) ---- */}
            <Card
              size="small"
              title="Staff Performance"
              style={{ marginTop: 16 }}
              variant="outlined"
            >
              {staffPerformance.length > 0 ? (
                <List
                  size="small"
                  dataSource={staffPerformance}
                  renderItem={(staff) => (
                    <List.Item>
                      <Space
                        size="large"
                        style={{ width: '100%', justifyContent: 'space-between' }}
                      >
                        <Tag color="green">{staff.staffName}</Tag>
                        <Text>Rooms cleaned: {staff.roomsCleaned}</Text>
                        <Text>
                          Time spent:{' '}
                          {staff.timeSpentHours
                            ? staff.timeSpentHours.toFixed(2)
                            : 0}{' '}
                          hrs
                        </Text>
                        <Text>Issues reported: {staff.issuesReported}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert
                  message="No staff performance data found for this period."
                  type="info"
                  showIcon
                />
              )}
            </Card>

            {/* ---- Detailed list of rooms cleaned ---- */}
            {deepDiagnosticData && (
              <Card
                size="small"
                title="Rooms Cleaned (detailed)"
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                {cleanedTasks.length > 0 ? (
                  <List
                    size="small"
                    dataSource={cleanedTasks}
                    renderItem={(task) => {
                      const durationMinutes =
                        task.CompletedAt && task.AssignedAt
                          ? Math.max(
                              0,
                              moment(task.CompletedAt).diff(
                                moment(task.AssignedAt),
                                'minutes'
                              )
                            )
                          : null;

                      return (
                        <List.Item>
                          <Descriptions size="small" column={4} style={{ width: '100%' }}>
                            <Descriptions.Item label="Room">
                              <Tag color="blue">Room {task.RoomNumber}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Cleaned By">
                              <Tag color="green">{task.UserName}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Completed At">
                              {task.CompletedAt
                                ? moment(task.CompletedAt).format(
                                    'MM/DD/YYYY HH:mm'
                                  )
                                : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Duration">
                              {durationMinutes != null
                                ? `${durationMinutes} min`
                                : '—'}
                            </Descriptions.Item>
                          </Descriptions>
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <Alert
                    message="No completed rooms found in the current data sample."
                    type="info"
                    showIcon
                  />
                )}
              </Card>
            )}
          </Card>
        )}

        {/* ---- Deep diagnostic section ---- */}
        {deepDiagnosticData && (
          <div>
            {/* Summary Statistics */}
            <Card
              title="Data Health Summary"
              style={{ marginBottom: 16 }}
              variant="outlined"
            >
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Statistic
                    title="Total Tasks"
                    value={deepDiagnosticData.taskAnalysis?.totalTasks || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Tasks with Rooms"
                    value={deepDiagnosticData.taskAnalysis?.tasksWithRoom || 0}
                    valueStyle={{
                      color:
                        deepDiagnosticData.taskAnalysis?.tasksWithRoom ===
                        deepDiagnosticData.taskAnalysis?.totalTasks
                          ? '#3f8600'
                          : '#cf1322'
                    }}
                    suffix={`/ ${
                      deepDiagnosticData.taskAnalysis?.totalTasks || 0
                    }`}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Tasks with Users"
                    value={deepDiagnosticData.taskAnalysis?.tasksWithUser || 0}
                    valueStyle={{
                      color:
                        deepDiagnosticData.taskAnalysis?.tasksWithUser ===
                        deepDiagnosticData.taskAnalysis?.totalTasks
                          ? '#3f8600'
                          : '#cf1322'
                    }}
                    suffix={`/ ${
                      deepDiagnosticData.taskAnalysis?.totalTasks || 0
                    }`}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Complete Relationships"
                    value={
                      deepDiagnosticData.taskAnalysis
                        ?.tasksWithBothRelationships || 0
                    }
                    valueStyle={{
                      color:
                        deepDiagnosticData.taskAnalysis
                          ?.tasksWithBothRelationships ===
                        deepDiagnosticData.taskAnalysis?.totalTasks
                          ? '#3f8600'
                          : '#cf1322'
                    }}
                    suffix={`/ ${
                      deepDiagnosticData.taskAnalysis?.totalTasks || 0
                    }`}
                  />
                </Col>
              </Row>
            </Card>

            {/* Issues Breakdown */}
            <Card
              title="Identified Issues"
              style={{ marginBottom: 16 }}
              variant="outlined"
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Card size="small" style={{ border: '1px solid #ff4d4f' }}>
                    <Statistic
                      title="Missing Room Relationships"
                      value={
                        deepDiagnosticData.issues?.missingRooms?.length || 0
                      }
                      valueStyle={{ color: '#cf1322' }}
                      prefix={<WarningOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" style={{ border: '1px solid #ff4d4f' }}>
                    <Statistic
                      title="Missing User Relationships"
                      value={
                        deepDiagnosticData.issues?.missingUsers?.length || 0
                      }
                      valueStyle={{ color: '#cf1322' }}
                      prefix={<WarningOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" style={{ border: '1px solid #ffa940' }}>
                    <Statistic
                      title="Hotel ID Mismatches"
                      value={
                        (deepDiagnosticData.issues?.roomHotelMismatch?.length ||
                          0) +
                        (deepDiagnosticData.issues?.userHotelMismatch?.length ||
                          0)
                      }
                      valueStyle={{ color: '#fa8c16' }}
                      prefix={<WarningOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* Sample Tasks with Issues */}
            <Card
              title="Problem Tasks"
              variant="outlined"
              style={{ marginBottom: 16 }}
            >
              {deepDiagnosticData.taskAnalysis?.sampleTasks &&
              deepDiagnosticData.taskAnalysis.sampleTasks.length > 0 ? (
                <List
                  dataSource={deepDiagnosticData.taskAnalysis.sampleTasks}
                  renderItem={(task) => (
                    <List.Item>
                      <Card
                        size="small"
                        style={{
                          width: '100%',
                          border:
                            task.RoomExists && task.UserExists
                              ? '1px solid #d9d9d9'
                              : '2px solid #ff4d4f',
                          backgroundColor:
                            task.RoomExists && task.UserExists
                              ? 'white'
                              : '#fff2f0'
                        }}
                      >
                        <Descriptions size="small" column={3}>
                          <Descriptions.Item label="Task ID">
                            {task.TaskId}
                          </Descriptions.Item>
                          <Descriptions.Item label="Status">
                            <Tag
                              color={
                                task.Status === 'Completed'
                                  ? 'green'
                                  : task.Status === 'Pending'
                                  ? 'orange'
                                  : 'blue'
                              }
                            >
                              {task.Status}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Assigned">
                            {moment(task.AssignedAt).format('MM/DD/YYYY')}
                          </Descriptions.Item>
                          <Descriptions.Item label="Room">
                            <Tag color={task.RoomExists ? 'green' : 'red'}>
                              {task.RoomNumber}{' '}
                              {!task.RoomExists && `(ID: ${task.RoomId})`}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Staff">
                            <Tag color={task.UserExists ? 'green' : 'red'}>
                              {task.UserName}{' '}
                              {!task.UserExists &&
                                task.UserId &&
                                `(ID: ${task.UserId})`}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Hotel ID">
                            <Tag
                              color={
                                task.HotelId === hotelId ? 'green' : 'red'
                              }
                            >
                              {task.HotelId}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert
                  message="No Tasks Found"
                  description="No housekeeping tasks were found for this hotel."
                  type="warning"
                  showIcon
                />
              )}
            </Card>

            {/* Available Resources */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="Available Rooms" variant="outlined">
                  {deepDiagnosticData.availableRooms &&
                  deepDiagnosticData.availableRooms.length > 0 ? (
                    <List
                      size="small"
                      dataSource={deepDiagnosticData.availableRooms}
                      renderItem={(room) => (
                        <List.Item>
                          <Tag color="blue">Room {room.RoomNumber}</Tag>
                          <Text type="secondary">
                            (ID: {room.RoomId})
                          </Text>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Alert
                      message="No rooms found"
                      type="warning"
                      showIcon
                    />
                  )}
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Available Staff" variant="outlined">
                  {deepDiagnosticData.availableUsers &&
                  deepDiagnosticData.availableUsers.length > 0 ? (
                    <List
                      size="small"
                      dataSource={deepDiagnosticData.availableUsers}
                      renderItem={(user) => (
                        <List.Item>
                          <Tag color="green">
                            {user.FirstName} {user.LastName}
                          </Tag>
                          <Text type="secondary">(ID: {user.Id})</Text>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Alert
                      message="No staff found"
                      type="warning"
                      showIcon
                    />
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Fix Data Modal */}
        <Modal
          title="Fix Data Issues"
          open={showFixModal}
          onOk={fixDataIssues}
          onCancel={() => setShowFixModal(false)}
          confirmLoading={fixing}
          okText="Fix Issues"
          cancelText="Cancel"
          okType="danger"
        >
          <Alert
            message="Data Fixing Operation"
            description="This will attempt to fix missing Room and User relationships by assigning available rooms and staff from your hotel. This operation cannot be undone."
            type="warning"
            showIcon
          />
          <div style={{ marginTop: 16 }}>
            <Text strong>This will fix:</Text>
            <ul>
              <li>Tasks without Room relationships</li>
              <li>Tasks without User relationships</li>
              <li>Assign fallback rooms and staff where needed</li>
            </ul>
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default HousekeepingReportsPage;
